/*
 * astro-story-lp.js
 *
 * Behavior for the long-form astro story LP:
 * - evergreen price-hold countdown (per visitor via localStorage; restarts once expired)
 * - sticky mobile CTA that hides while the offer grid is on screen
 * - scroll-reveal for .as-reveal blocks (skipped under reduced motion)
 * - dataLayer events: story_viewed, story_cta_clicked {pos}, story_offer_viewed
 */

const LS_KEY = 'astro_story_deadline';

function analytics(event, payload) {
  if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event, ...(payload || {}) });
}

// Deadline for this visitor: reuse a live one, otherwise start a fresh window.
export function resolveDeadline(storedIso, nowMs, minutes) {
  const stored = storedIso ? Date.parse(storedIso) : NaN;
  if (!Number.isNaN(stored) && stored > nowMs) return new Date(stored).toISOString();
  return new Date(nowMs + minutes * 60000).toISOString();
}

export function formatMmSs(msRemaining) {
  const total = Math.max(0, Math.floor(msRemaining / 1000));
  return Math.floor(total / 60) + ':' + String(total % 60).padStart(2, '0');
}

function initCountdown(root) {
  const wrap = root.querySelector('[data-as-timer-wrap]');
  const out = root.querySelector('[data-as-timer]');
  if (!wrap || !out || root.dataset.showCountdown !== 'true') return;
  const minutes = parseInt(root.dataset.countdownMinutes || '15', 10) || 15;

  let stored = null;
  try { stored = localStorage.getItem(LS_KEY); } catch (e) {}
  const deadlineIso = resolveDeadline(stored, Date.now(), minutes);
  try { localStorage.setItem(LS_KEY, deadlineIso); } catch (e) {}
  const deadline = Date.parse(deadlineIso);

  wrap.hidden = false;
  function tick() {
    const left = deadline - Date.now();
    out.textContent = formatMmSs(left);
    if (left <= 0) { wrap.hidden = true; try { localStorage.removeItem(LS_KEY); } catch (e) {} return; }
    setTimeout(tick, 1000);
  }
  tick();
}

function initSticky(root) {
  const sticky = root.querySelector('[data-as-sticky]');
  const offer = root.querySelector('[data-as-offer]');
  const hero = root.querySelector('.as-hero');
  if (!sticky || !offer) return;

  let pastHero = false;
  let offerOnScreen = false;
  function apply() { sticky.hidden = !(pastHero && !offerOnScreen); }

  if (!('IntersectionObserver' in window)) return;
  new IntersectionObserver((entries) => {
    entries.forEach((en) => { pastHero = !en.isIntersecting; });
    apply();
  }).observe(hero || root);
  new IntersectionObserver((entries) => {
    entries.forEach((en) => {
      offerOnScreen = en.isIntersecting;
      if (en.isIntersecting) analytics('story_offer_viewed', {});
    });
  }, { threshold: 0.15 }).observe(offer);
}

function initReveals(root) {
  const blocks = root.querySelectorAll('.as-reveal');
  if (!blocks.length) return;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced || !('IntersectionObserver' in window)) {
    blocks.forEach((b) => b.classList.add('is-in'));
    return;
  }
  const io = new IntersectionObserver((entries) => {
    entries.forEach((en) => {
      if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); }
    });
  }, { threshold: 0.12 });
  blocks.forEach((b) => io.observe(b));
}

function initCtas(root) {
  root.querySelectorAll('[data-as-cta]').forEach((a) => {
    a.addEventListener('click', () => analytics('story_cta_clicked', { pos: a.dataset.asCtaPos || '' }));
  });
}

function init() {
  const root = document.querySelector('[data-as-root]');
  if (!root) return;
  // Preview override: ?as_theme=light|dark flips the palette without touching settings.
  const themeOverride = new URLSearchParams(location.search).get('as_theme');
  if (themeOverride === 'light') root.classList.add('as--light');
  if (themeOverride === 'dark') root.classList.remove('as--light');
  analytics('story_viewed', { theme: root.classList.contains('as--light') ? 'light' : 'dark' });
  initCountdown(root);
  initSticky(root);
  initReveals(root);
  initCtas(root);
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
}
